/**
 * Показатели нарядов-допусков (КЧ-05): время согласования и выполнения, возвраты, продления, превышения в анализе
 * воздуха, остановки — за выбранный период, всего и по видам работ. Считает сервер (`GET /quality/permits/stats`).
 */
import { PERMIT_KINDS, type PermitStats, type PermitStatsRow } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { todayLocal } from '../../lib/format';
import { ErrorBox, Spinner } from '../../ui/feedback';
import { Select } from '../../ui/form';
import { Panel } from '../../ui/layout';

const PERIODS = [
  { days: 30, label: '30 дней' },
  { days: 90, label: '90 дней' },
  { days: 365, label: 'год' },
] as const;

const minusDays = (day: string, n: number) => {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - n + 1);
  return d.toISOString().slice(0, 10);
};

const hoursText = (h: number | null, n: number) =>
  h === null ? '—' : `${h.toLocaleString('ru-RU')} ч${n > 1 ? ` (по ${n})` : ''}`;

const kindTitle = (k: PermitStatsRow['kind']) => (k === 'all' ? 'Все виды' : PERMIT_KINDS[k].title);

function Tile({ label, value, warn }: { label: string; value: string | number; warn?: boolean }) {
  return (
    <div className="rounded-md border border-line bg-surface-2 p-3">
      <div className="text-[13px] text-ink-2">{label}</div>
      <div className={`num text-[20px] font-semibold ${warn ? 'text-danger' : 'text-ink'}`}>{value}</div>
    </div>
  );
}

export function PermitStatsPanel({ projectId }: { projectId?: string }) {
  const [days, setDays] = useState<number>(30);
  const to = todayLocal();
  const from = minusDays(to, days);
  const q = useQuery({
    queryKey: ['qa-permit-stats', projectId ?? '', from, to],
    queryFn: () => api<PermitStats>('/quality/permits/stats', { query: { projectId, from, to } }),
  });
  const all = q.data?.rows.find((r) => r.kind === 'all');
  const kinds = q.data?.rows.filter((r) => r.kind !== 'all') ?? [];
  return (
    <Panel
      title="Показатели нарядов"
      className="mb-4"
      actions={
        <Select
          aria-label="Период"
          value={String(days)}
          onChange={(e) => setDays(Number(e.target.value))}
          className="h-11 w-auto"
        >
          {PERIODS.map((p) => (
            <option key={p.days} value={p.days}>
              {p.label}
            </option>
          ))}
        </Select>
      }
    >
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : !all?.permits ? (
        <p className="text-[14px] text-ink-2">За этот период по нарядам ничего не происходило.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <Tile label="Нарядов в работе за период" value={all.permits} />
            <Tile label="Согласование в среднем" value={hoursText(all.approvalHours, all.approvalCount)} />
            <Tile label="Выполнение в среднем" value={hoursText(all.executionHours, all.executionCount)} />
            <Tile label="Закрыто" value={all.closed} />
            <Tile label="Возвраты в работу" value={all.returned} warn={all.returned > 0} />
            <Tile label="Продления" value={all.extended} />
            <Tile label="Превышения в анализе воздуха" value={all.gasExceeded} warn={all.gasExceeded > 0} />
            <Tile label="Остановки работ" value={all.suspended} warn={all.suspended > 0} />
          </div>
          {kinds.length > 1 ? (
            <ul className="mt-3 flex flex-col gap-1.5 text-[14px]">
              {kinds.map((r) => (
                <li
                  key={r.kind}
                  className="flex flex-col gap-0.5 border-line border-t pt-1.5 md:flex-row md:gap-3"
                >
                  <span className="font-medium text-ink md:w-56">{kindTitle(r.kind)}</span>
                  <span className="text-ink-2">
                    нарядов {r.permits} · согласование {hoursText(r.approvalHours, r.approvalCount)} ·
                    выполнение {hoursText(r.executionHours, r.executionCount)} · возвратов {r.returned} ·
                    продлений {r.extended} · превышений {r.gasExceeded} · остановок {r.suspended}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      )}
    </Panel>
  );
}
